// What the document's head says per route: pure, so it runs under Node.
// The app is one page on every address (the CDN serves index.html for all
// of them), so what a crawler can tell the routes apart by is written here
// and applied by index.tsx on every pathname change.

export interface BrandText {
  name: string;
  /** One sentence; a trailing full stop is dropped in the title. */
  tagline: string;
}

/** The public routes: the landing page, and the root, which shows it to a visitor without data. */
const INDEXABLE = new Set(['/', '/welcome']);

/** Screen names by route; a route not here gets the name alone. `/review` covers every review address. */
const SCREENS: ReadonlyArray<readonly [route: string, screen: string]> = [
  ['/', 'Home'],
  ['/game', 'Home'],
  ['/overview', 'Overview'],
  ['/openings', 'Openings'],
  ['/middlegame', 'Middlegame'],
  ['/ending', 'Ending'],
  ['/blunders', 'Blunders'],
  ['/misses', 'Misses'],
  ['/library', 'Library'],
  ['/settings', 'Settings'],
  ['/sync', 'Syncing'],
  ['/review', 'Review'],
];

/** The pathname without its trailing slash (`/library/` is `/library`); `/` and '' are `/`. */
export function normalize(pathname: string): string {
  const p = pathname.replace(/\/+$/, '');
  return p === '' ? '/' : p;
}

/** The screen a pathname shows, or undefined for one the app does not know. */
export function screenFor(pathname: string): string | undefined {
  const p = normalize(pathname);
  for (const [route, screen] of SCREENS) {
    if (p === route || (route !== '/' && p.startsWith(`${route}/`))) return screen;
  }
  return undefined;
}

/**
 * The document title: "<Screen> · <name>"; the landing page says what the
 * app is, "<name> · <tagline>"; an unknown path, the name alone.
 */
export function titleFor(pathname: string, brand: BrandText): string {
  const p = normalize(pathname);
  if (p === '/welcome') return `${brand.name} · ${brand.tagline.replace(/\.$/, '')}`;
  const screen = screenFor(p);
  return screen ? `${screen} · ${brand.name}` : brand.name;
}

/** Whether a crawler may index the route: only the landing page and the root. */
export function indexable(pathname: string): boolean {
  return INDEXABLE.has(normalize(pathname));
}

/** The `<meta name="robots">` content for the route; links are followed everywhere. */
export function robotsFor(pathname: string): 'index,follow' | 'noindex,follow' {
  return indexable(pathname) ? 'index,follow' : 'noindex,follow';
}

/**
 * The canonical address of an indexable route, or undefined: `base` is the
 * site's origin (no trailing slash), the root's canonical is `base/`.
 */
export function canonicalFor(pathname: string, base: string): string | undefined {
  if (!indexable(pathname)) return undefined;
  const p = normalize(pathname);
  return `${base.replace(/\/+$/, '')}${p === '/' ? '/' : p}`;
}
